import React from "react";
import styled from "styled-components";
import { Calendar, ArrowLeft } from "lucide-react";
import { router } from "@inertiajs/react";
import LeftSidebar from "../../components/left-sidebar";
import TrendingPanel from "../../components/trending-panel";
import {
  AppLayout,
  MainArea,
  FeedColumn,
  RightSidebar,
  MobileHeader,
  MobileTitle,
} from "../../constants/styles";
import { apiGet, apiPost, apiDelete } from "../../../api";
import { renderContent } from "../../utils/render-content";

function TabContent({ slug, endpoint, emptyTitle, emptyDesc }) {
  const [items, setItems] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    apiGet(`/api/v1/communities/users/${slug}/${endpoint}`)
      .then((res) => {
        if (res.parsedResponse?.data) setItems(res.parsedResponse.data);
      })
      .finally(() => setLoaded(true));
  }, [slug, endpoint]);

  if (!loaded)
    return (
      <div
        style={{
          padding: "2rem",
          textAlign: "center",
          color: "var(--muted-foreground)",
        }}
      >
        Loading...
      </div>
    );
  if (items.length === 0)
    return (
      <div
        style={{
          padding: "2rem",
          textAlign: "center",
          color: "var(--muted-foreground)",
        }}
      >
        <h3 style={{ fontWeight: 600 }}>{emptyTitle}</h3>
        <p>{emptyDesc}</p>
      </div>
    );

  return items.map((thread) => (
    <article
      key={thread.id}
      style={{
        padding: "1rem",
        borderBottom: "1px solid var(--border)",
        cursor: "pointer",
      }}
      onClick={() => router.visit(`/communities/threads/${thread.id}`)}
    >
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "var(--primary, #ff9900)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontWeight: 700,
          }}
        >
          {thread.author?.name?.charAt(0) || "?"}
        </div>
        <div>
          <span style={{ fontWeight: 700 }}>{thread.author?.name}</span>{" "}
          <span
            style={{ color: "var(--muted-foreground)", fontSize: "0.875rem" }}
          >
            {thread.author?.handle} · {thread.timestamp}
          </span>
        </div>
      </div>
      {thread.parent_preview && (
        <div
          style={{
            fontSize: "0.8rem",
            color: "var(--muted-foreground)",
            marginBottom: "0.5rem",
            paddingLeft: "0.5rem",
            borderLeft: "2px solid var(--border)",
          }}
        >
          Replying to {thread.parent_preview.author?.handle}:{" "}
          {thread.parent_preview.content}
        </div>
      )}
      <div style={{ lineHeight: 1.5 }}>{renderContent(thread.content)}</div>
    </article>
  ));
}

const BackButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--foreground);

  &:hover {
    background: var(--secondary);
  }
`;

const HeaderBar = styled.div`
  display: none;
  align-items: center;
  gap: 1.5rem;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--border);
  background: var(--card);
  position: sticky;
  top: 0;
  z-index: 40;

  @media (min-width: 720px) {
    display: flex;
  }
`;

const HeaderInfo = styled.div``;

const HeaderTitle = styled.h1`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--foreground);
`;

const HeaderSubtitle = styled.p`
  font-size: 0.8125rem;
  color: var(--muted-foreground);
`;

// Profile Header Section
const ProfileBanner = styled.div`
  height: 150px;
  background: linear-gradient(135deg, var(--primary), #ffb347);

  @media (min-width: 720px) {
    height: 200px;
  }
`;

const ProfileInfo = styled.div`
  padding: 0 1rem 1rem;
  background: var(--card);
  border-bottom: 1px solid var(--border);
`;

const ProfileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: -3.5rem;
  margin-bottom: 1rem;
`;

const ProfileAvatar = styled.div`
  width: 84px;
  height: 84px;
  border-radius: 50%;
  border: 4px solid var(--card);
  background: linear-gradient(135deg, var(--primary), #ffb347);
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-size: 2rem;
    font-weight: 700;
    color: white;
  }

  @media (min-width: 720px) {
    width: 133px;
    height: 133px;
    margin-top: -1rem;

    span {
      font-size: 3rem;
    }
  }
`;

const EditButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  border: 1px solid var(--border);
  background: transparent;
  color: var(--foreground);
  font-weight: 700;
  font-size: 0.9375rem;
  cursor: pointer;
  margin-top: 3.5rem;

  &:hover {
    background: var(--secondary);
  }

  @media (min-width: 720px) {
    margin-top: 0.75rem;
  }
`;

const ProfileName = styled.h1`
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--foreground);

  @media (min-width: 720px) {
    font-size: 1.5rem;
  }
`;

const ProfileHandle = styled.p`
  font-size: 0.9375rem;
  color: var(--muted-foreground);
`;

const ProfileBio = styled.p`
  margin-top: 0.75rem;
  font-size: 0.9375rem;
  color: var(--foreground);
  line-height: 1.5;
`;

const ProfileMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.75rem;
  font-size: 0.9375rem;
  color: var(--muted-foreground);
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;

  a {
    color: var(--primary);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ProfileStats = styled.div`
  display: flex;
  gap: 1.25rem;
  margin-top: 0.75rem;
`;

const Stat = styled.span`
  font-size: 0.9375rem;
  color: var(--muted-foreground);

  strong {
    color: var(--foreground);
    font-weight: 700;
  }
`;

// Tabs
const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid var(--border);
  background: var(--card);
  overflow-x: auto;
`;

const Tab = styled.button`
  flex: 1;
  min-width: fit-content;
  padding: 1rem;
  background: transparent;
  border: none;
  font-size: 0.9375rem;
  font-weight: ${(props) => (props.$active ? "700" : "500")};
  color: ${(props) =>
    props.$active ? "var(--foreground)" : "var(--muted-foreground)"};
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: var(--secondary);
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: ${(props) => (props.$active ? "56px" : "0")};
    height: 4px;
    background: var(--primary);
    border-radius: 9999px;
    transition: width 0.2s;
  }
`;

// Thread Card (for Posts tab)
const Article = styled.article`
  border-bottom: 1px solid var(--border);
  padding: 1rem;
  background: var(--card);
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: var(--secondary);
  }
`;

const ThreadContent = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary), #ffb347);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  span {
    font-size: 0.875rem;
    font-weight: 700;
    color: white;
  }
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ThreadHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-wrap: wrap;
`;

const Name = styled.span`
  font-weight: 700;
  color: var(--foreground);
`;

const Handle = styled.span`
  color: var(--muted-foreground);
  font-size: 0.9375rem;
`;

const Dot = styled.span`
  color: var(--muted-foreground);
`;

const Timestamp = styled.span`
  color: var(--muted-foreground);
  font-size: 0.9375rem;
`;

const TextContent = styled.p`
  margin-top: 0.25rem;
  font-size: 0.9375rem;
  color: var(--foreground);
  line-height: 1.5;
`;

// Empty State
const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--muted-foreground);
`;

const EmptyTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--foreground);
  margin-bottom: 0.5rem;
`;

// Activity Tab Styles
const ActivitySection = styled.div`
  padding: 1rem;
  border-bottom: 1px solid var(--border);
  background: var(--card);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--foreground);
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @media (min-width: 720px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const SummaryCard = styled.div`
  background: var(--secondary);
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  padding: 1rem;
`;

const SummaryLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--muted-foreground);
  text-transform: uppercase;
  margin-bottom: 0.5rem;
`;

const SummaryValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--foreground);
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const BadgeCount = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  span.dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${(props) => props.$color};
  }

  span.count {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--foreground);
  }
`;

const BadgesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const BadgeCard = styled.div`
  background: var(--secondary);
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  padding: 1rem;
  text-align: center;
`;

const BadgeIcon = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
`;

const BadgeTitle = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--foreground);
`;

const BadgeSubtitle = styled.div`
  font-size: 0.8125rem;
  color: var(--muted-foreground);
`;

// Mobile Nav
export default function ProfilePage({ slug }) {
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState("activity");
  const [profile, setProfile] = React.useState(null);
  const [userPosts, setUserPosts] = React.useState([]);
  const [badges, setBadges] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [followLoading, setFollowLoading] = React.useState(false);

  React.useEffect(() => {
    if (!slug) return;
    setLoading(true);
    apiGet(`/api/v1/communities/users/${slug}`)
      .then((res) => {
        if (res.statusCode === 404) {
          setError("User not found.");
          return;
        }
        if (res.parsedResponse) {
          setProfile(res.parsedResponse.user);
          setUserPosts(res.parsedResponse.threads || []);
          setBadges(res.parsedResponse.badges || []);
        }
      })
      .catch(() => setError("Failed to load profile."))
      .finally(() => setLoading(false));
  }, [slug]);

  const toggleFollow = async () => {
    if (!profile) return;
    setFollowLoading(true);
    try {
      if (profile.is_following) {
        await apiDelete(`/api/v1/communities/users/${profile.id}/follow`);
        setProfile((p) => ({
          ...p,
          is_following: false,
          followers_count: p.followers_count - 1,
        }));
      } else {
        await apiPost(`/api/v1/communities/users/${profile.id}/follow`);
        setProfile((p) => ({
          ...p,
          is_following: true,
          followers_count: p.followers_count + 1,
        }));
      }
    } finally {
      setFollowLoading(false);
    }
  };

  const isSelf = profile?.is_self;
  const [hiddenPosts, setHiddenPosts] = React.useState([]);
  const [hiddenPostsLoaded, setHiddenPostsLoaded] = React.useState(false);

  const loadHiddenPosts = () => {
    if (hiddenPostsLoaded) return;
    apiGet("/api/v1/communities/users/my_flagged_threads")
      .then((res) => {
        if (res.parsedResponse?.data) setHiddenPosts(res.parsedResponse.data);
      })
      .finally(() => setHiddenPostsLoaded(true));
  };

  const tabs = isSelf
    ? ["Activity", "Posts", "Replies", "Media", "Likes", "Hidden Posts"]
    : ["Activity", "Posts", "Replies", "Media", "Likes"];

  const silverBadges = badges.filter((b) => b.badge_type === "silver");
  const bronzeBadges = badges.filter((b) => b.badge_type === "bronze");
  const goldBadges = badges.filter((b) => b.badge_type === "gold");

  return (
    <AppLayout>
      <LeftSidebar
        onCreatePost={() => {}}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />

      <MainArea>
        <FeedColumn>
          {/* Mobile Header */}
          <MobileHeader>
            <BackButton onClick={() => window.history.back()}>
              <ArrowLeft size={20} />
            </BackButton>
            <MobileTitle>{profile?.name || slug}</MobileTitle>
          </MobileHeader>

          {/* Desktop Header */}
          <HeaderBar>
            <BackButton onClick={() => window.history.back()}>
              <ArrowLeft size={20} />
            </BackButton>
            <HeaderInfo>
              <HeaderTitle>{profile?.name || slug}</HeaderTitle>
              <HeaderSubtitle>{userPosts.length} posts</HeaderSubtitle>
            </HeaderInfo>
          </HeaderBar>

          {loading && (
            <div
              style={{
                padding: "3rem",
                textAlign: "center",
                color: "var(--muted-foreground)",
              }}
            >
              Loading...
            </div>
          )}
          {error && (
            <div
              style={{ padding: "3rem", textAlign: "center", color: "#ef4444" }}
            >
              {error}
            </div>
          )}

          {!loading && !error && profile && (
            <>
              {/* Profile Banner */}
              <ProfileBanner />

              {/* Profile Info */}
              <ProfileInfo>
                <ProfileHeader>
                  <ProfileAvatar>
                    {profile.avatar_url ? (
                      <img
                        src={profile.avatar_url}
                        alt={profile.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "50%",
                        }}
                      />
                    ) : (
                      <span>{profile.name?.charAt(0)}</span>
                    )}
                  </ProfileAvatar>
                  {profile.is_self ? (
                    <EditButton
                      onClick={() =>
                        (window.location.href = "/communities/settings")
                      }
                    >
                      Edit profile
                    </EditButton>
                  ) : (
                    <EditButton onClick={toggleFollow} disabled={followLoading}>
                      {profile.is_following ? "Following" : "Follow"}
                    </EditButton>
                  )}
                </ProfileHeader>

                <ProfileName>{profile.name}</ProfileName>
                <ProfileHandle>{profile.handle}</ProfileHandle>

                {profile.bio && <ProfileBio>{profile.bio}</ProfileBio>}

                <ProfileMeta>
                  {profile.position && (
                    <MetaItem>
                      {profile.position}
                      {profile.company ? ` · ${profile.company}` : ""}
                    </MetaItem>
                  )}
                  <MetaItem>
                    <Calendar size={16} />
                    Joined {profile.joined_at}
                  </MetaItem>
                </ProfileMeta>

                <ProfileStats>
                  <Stat>
                    <strong>{profile.following_count}</strong> Following
                  </Stat>
                  <Stat>
                    <strong>{profile.followers_count.toLocaleString()}</strong>{" "}
                    Followers
                  </Stat>
                  {profile.reputation > 0 && (
                    <Stat>
                      <strong>{profile.reputation.toLocaleString()}</strong>{" "}
                      Reputation
                    </Stat>
                  )}
                </ProfileStats>
              </ProfileInfo>

              {/* Tabs */}
              <TabsContainer>
                {tabs.map((tab) => (
                  <Tab
                    key={tab}
                    $active={activeTab === tab.toLowerCase()}
                    onClick={() => setActiveTab(tab.toLowerCase())}
                  >
                    {tab}
                  </Tab>
                ))}
              </TabsContainer>

              {/* Activity Tab */}
              {activeTab === "activity" && (
                <>
                  <ActivitySection>
                    <SectionTitle>Summary</SectionTitle>
                    <SummaryGrid>
                      <SummaryCard>
                        <SummaryLabel>Reputation</SummaryLabel>
                        <SummaryValue>
                          {profile.reputation.toLocaleString()}
                        </SummaryValue>
                      </SummaryCard>
                      <SummaryCard>
                        <SummaryLabel>Badges</SummaryLabel>
                        <BadgeRow>
                          {goldBadges.length > 0 && (
                            <BadgeCount $color="#fbbf24">
                              <span className="dot" />
                              <span className="count">{goldBadges.length}</span>
                            </BadgeCount>
                          )}
                          {silverBadges.length > 0 && (
                            <BadgeCount $color="#94a3b8">
                              <span className="dot" />
                              <span className="count">
                                {silverBadges.length}
                              </span>
                            </BadgeCount>
                          )}
                          {bronzeBadges.length > 0 && (
                            <BadgeCount $color="#cd7f32">
                              <span className="dot" />
                              <span className="count">
                                {bronzeBadges.length}
                              </span>
                            </BadgeCount>
                          )}
                          {badges.length === 0 && (
                            <span
                              style={{
                                color: "var(--muted-foreground)",
                                fontSize: "0.875rem",
                              }}
                            >
                              No badges yet
                            </span>
                          )}
                        </BadgeRow>
                      </SummaryCard>
                      <SummaryCard>
                        <SummaryLabel>Posts</SummaryLabel>
                        <SummaryValue>{userPosts.length}</SummaryValue>
                      </SummaryCard>
                    </SummaryGrid>
                  </ActivitySection>

                  {badges.length > 0 && (
                    <ActivitySection>
                      <SectionHeader>
                        <SectionTitle>Badges</SectionTitle>
                      </SectionHeader>
                      <BadgesGrid>
                        {badges.map((badge) => (
                          <BadgeCard key={badge.id}>
                            <BadgeIcon>
                              {badge.badge_type === "gold"
                                ? "🥇"
                                : badge.badge_type === "silver"
                                  ? "🥈"
                                  : "🥉"}
                            </BadgeIcon>
                            <BadgeTitle>{badge.name}</BadgeTitle>
                            <BadgeSubtitle>{badge.description}</BadgeSubtitle>
                          </BadgeCard>
                        ))}
                      </BadgesGrid>
                    </ActivitySection>
                  )}
                </>
              )}

              {/* Posts Tab */}
              {activeTab === "posts" &&
                (userPosts.length > 0 ? (
                  userPosts.map((thread) => (
                    <Article
                      key={thread.id}
                      onClick={() =>
                        router.visit(`/communities/threads/${thread.id}`)
                      }
                    >
                      <ThreadContent>
                        <Avatar>
                          <span>{profile.name?.charAt(0)}</span>
                        </Avatar>
                        <MainContent>
                          <ThreadHeader>
                            <Name>{profile.name}</Name>
                            <Handle>{profile.handle}</Handle>
                            <Dot>·</Dot>
                            <Timestamp>{thread.timestamp}</Timestamp>
                          </ThreadHeader>
                          <TextContent>{thread.content}</TextContent>
                        </MainContent>
                      </ThreadContent>
                    </Article>
                  ))
                ) : (
                  <EmptyState>
                    <EmptyTitle>No posts yet</EmptyTitle>
                    <p>
                      When {profile.name} posts, they{"'"}ll show up here.
                    </p>
                  </EmptyState>
                ))}

              {activeTab === "replies" && (
                <TabContent
                  slug={slug}
                  endpoint="replies"
                  emptyTitle="No replies yet"
                  emptyDesc={`When ${profile.name} replies, they'll show up here.`}
                />
              )}

              {activeTab === "media" && (
                <TabContent
                  slug={slug}
                  endpoint="media"
                  emptyTitle="No media yet"
                  emptyDesc="Photos and videos will appear here."
                />
              )}

              {activeTab === "likes" && (
                <TabContent
                  slug={slug}
                  endpoint="likes"
                  emptyTitle="No likes yet"
                  emptyDesc="Liked posts will appear here."
                />
              )}

              {/* Hidden Posts Tab — only visible to self */}
              {activeTab === "hidden posts" && isSelf && (
                <>
                  {!hiddenPostsLoaded && loadHiddenPosts()}
                  {hiddenPosts.length > 0 ? (
                    hiddenPosts.map((thread) => (
                      <Article key={thread.id} style={{ opacity: 0.7 }}>
                        <ThreadContent>
                          <Avatar>
                            <span>{profile.name?.charAt(0)}</span>
                          </Avatar>
                          <MainContent>
                            <ThreadHeader>
                              <Name>{profile.name}</Name>
                              <Handle>{profile.handle}</Handle>
                            </ThreadHeader>
                            <div
                              style={{
                                background: "#fff3cd",
                                border: "1px solid #ffc107",
                                borderRadius: "6px",
                                padding: "6px 10px",
                                marginBottom: "6px",
                                fontSize: "12px",
                                color: "#856404",
                              }}
                            >
                              ⚠️ Post ini disembunyikan oleh moderator
                              {thread.hidden_at &&
                                ` pada ${new Date(thread.hidden_at).toLocaleDateString("id-ID")}`}
                            </div>
                            <TextContent>{thread.content}</TextContent>
                          </MainContent>
                        </ThreadContent>
                      </Article>
                    ))
                  ) : (
                    <EmptyState>
                      <EmptyTitle>Tidak ada post yang disembunyikan</EmptyTitle>
                      <p>
                        Post yang disembunyikan oleh moderator akan muncul di
                        sini.
                      </p>
                    </EmptyState>
                  )}
                </>
              )}
            </>
          )}
        </FeedColumn>

        <RightSidebar>
          <TrendingPanel />
        </RightSidebar>
      </MainArea>
    </AppLayout>
  );
}
